import React from 'react'
import { CardBody } from '../../organisms/Card'
import {
  ChartContainerProps,
  ChartSectionProps,
  ChartGridProps,
  ChartLegendProps,
  ChartLegendItemProps,
  ColorBlockProps,
  ErrorContainerProps,
  NotAvailableProps,
} from './ChartComponents.types'
import {
  ChartContainer as StyledChartContainer,
  ChartSection as StyledChartSection,
  ChartTitle as StyledChartTitle,
  ChartGrid as StyledChartGrid,
  LegendContainer,
  LegendItem as StyledChartLegendItem,
  ColorBlock as StyledColorBlock,
  LegendLabel,
  ErrorContainer as StyledErrorContainer,
  NotAvailableContainer,
  NotAvailableTitle,
  NotAvailableText,
  PrintWrapper as StyledPrintWrapper,
} from './ChartComponents.styles'

export const ChartContainer = React.forwardRef<
  HTMLDivElement,
  ChartContainerProps
>(({ children, height, width, printHeight, className, style }, ref) => {
  return (
    <StyledChartContainer
      ref={ref}
      $height={height}
      $width={width}
      $printHeight={printHeight}
      className={className}
      style={style}
    >
      {children}
    </StyledChartContainer>
  )
})

ChartContainer.displayName = 'ChartContainer'

export const ChartSection: React.FC<ChartSectionProps> = ({
  children,
  title,
  className,
  style,
}) => {
  return (
    <StyledChartSection className={className}>
      <CardBody style={style}>
        {title && <StyledChartTitle>{title}</StyledChartTitle>}
        {children}
      </CardBody>
    </StyledChartSection>
  )
}

export const ChartGrid: React.FC<ChartGridProps> = ({
  children,
  columns,
  gap,
  className,
  style,
}) => {
  return (
    <StyledChartGrid
      $columns={columns}
      $gap={gap}
      className={className}
      style={style}
    >
      {children}
    </StyledChartGrid>
  )
}

export const ChartLegend: React.FC<ChartLegendProps> = ({
  items,
  className,
  style,
}) => {
  return (
    <LegendContainer className={className} style={style}>
      {items.map((item, index) => (
        <ChartLegendItem key={index} {...item} />
      ))}
    </LegendContainer>
  )
}

export const ChartLegendItem: React.FC<ChartLegendItemProps> = ({
  color,
  label,
}) => {
  return (
    <StyledChartLegendItem>
      <ColorBlock color={color} />
      <LegendLabel>{label}</LegendLabel>
    </StyledChartLegendItem>
  )
}

export const ColorBlock: React.FC<ColorBlockProps> = ({
  color,
  size = 'medium',
  className,
  style,
}) => {
  return (
    <StyledColorBlock
      $color={color}
      $size={size}
      className={className}
      style={style}
    />
  )
}

export const ErrorContainer: React.FC<ErrorContainerProps> = ({
  children,
  variant = 'error',
  className,
  style,
}) => {
  return (
    <StyledErrorContainer
      $variant={variant}
      className={className}
      style={style}
    >
      {children}
    </StyledErrorContainer>
  )
}

export const NotAvailable: React.FC<NotAvailableProps> = ({
  title = 'Not Available',
  description = 'This feature is not available at the current selection level.',
  className,
  style,
}) => {
  return (
    <NotAvailableContainer className={className} style={style}>
      <NotAvailableTitle>{title}</NotAvailableTitle>
      <NotAvailableText>{description}</NotAvailableText>
    </NotAvailableContainer>
  )
}

export const PrintWrapper: React.FC<ChartContainerProps> = ({
  children,
  className,
  style,
}) => {
  return (
    <StyledPrintWrapper className={className} style={style}>
      {children}
    </StyledPrintWrapper>
  )
}

// Export styled components for direct use if needed
export const ChartTitle = StyledChartTitle

// Re-export with original name for backward compatibility but mark as deprecated
/** @deprecated Use ChartLegend instead */
export const Legend = ChartLegend
